<template>
  <div class="rule-form-step rule-form-step--stop rule-form-step--stop--step-1 flex max-w-4xl flex-col gap-5 py-5">
    <name-block
      :entity="entity"
      :description="tr('This will be the name of your currently created automation. The name helps identify and easily manage the different automations you have set up.')"
      :disabled="$parent.readonlyMode"
    />

    <notifiable-users-block :entity="entity" :disabled="$parent.readonlyMode" />
    <activity-management-block :entity="entity" :disabled="$parent.readonlyMode" />

    <form-block :title="tr('AI Assist')">
      <label :class="[ 'flex w-fit items-center gap-2 text-sm text-foreground', $parent.readonlyMode ? 'cursor-not-allowed' : 'cursor-pointer' ]">
        <ui-checkbox
          :model-value="!!entity.settings.stop_outdated_ads"
          :disabled="$parent.readonlyMode"
          @update:model-value="(value) => entity.settings.stop_outdated_ads = value"
        />
        {{ tr('Stop outdated ads') }}
        <info-popup>
          <div v-html="tr('Automatically stop advertisements whose content becomes outdated.')" />
        </info-popup>
      </label>
    </form-block>

    <brand-block
      :entity="entity"
      :disabled="$parent.readonlyMode || !entity.isNew"
    />

    <operation-area-block :entity="entity" :disabled="$parent.readonlyMode" />

    <form-block
      :title="tr('Stop level')"
      :description="tr('Choose at which level the conditions are evaluated and the stop is performed.')"
    >
      <ui-radio-group
        v-model="entity.settings.level"
        :disabled="$parent.readonlyMode || !entity.isNew"
        class="flex flex-wrap gap-x-6 gap-y-2.5"
      >
        <label
          v-for="option in stopLevelOptions"
          :key="'stop-level--' + option.value"
          :class="[ 'flex w-fit items-center gap-2 text-sm text-foreground', $parent.readonlyMode || !entity.isNew ? 'cursor-not-allowed' : 'cursor-pointer' ]"
        >
          <ui-radio-group-item :value="option.value" />
          {{ option.label }}
          <info-popup>
            <div v-html="option.description" />
          </info-popup>
        </label>
      </ui-radio-group>
    </form-block>

    <post-type-block
      :entity="entity"
      :description="tr('Here you can select what type of posts and appearances should be handled by the automation. Please note that the automation will handle only those post ads that you created using :app.')"
      :disabled="$parent.readonlyMode"
    />

    <form-block
      :title="tr('Filters')"
      :description='tr("You can set different filters to determine the conditions under which the ads should stop or scaling up. You can add multiple conditions to an ad by clicking on the \"+\" sign, and you can connect them with the \"and\" or \"or\" options.")'
    >
      <condition-group
        v-model="entity.conditions"
        :action="entity.action"
        :platforms="entity.selectedSourcePlatforms"
        :ad-account="entity.selectedAdAccount"
        :disabled="!filterIsAvailable || $parent.readonlyMode"
      />
    </form-block>
  </div>
</template>

<script>
import { RULE_RUN_LEVEL } from '@/consts.js';
import RuleFormStepMixin from '@/mixins/RuleForm/Step.js';
import InfoPopup from '@/ui/components/InfoPopup.vue';
import ActivityManagementBlock from '@/ui/components/RuleForm/Blocks/ActivityManagementBlock.vue';
import BrandBlock from '@/ui/components/RuleForm/Blocks/BrandBlock.vue';
import NameBlock from '@/ui/components/RuleForm/Blocks/NameBlock.vue';
import NotifiableUsersBlock from '@/ui/components/RuleForm/Blocks/NotifiableUsersBlock.vue';
import OperationAreaBlock from '@/ui/components/RuleForm/Blocks/OperationAreaBlock.vue';
import PostTypeBlock from '@/ui/components/RuleForm/Blocks/PostTypeBlock.vue';
import ConditionGroup from '@/ui/components/RuleForm/Condition/ConditionGroup.vue';

export default {
  name: 'StopRuleFormStep1',
  components: {
    ActivityManagementBlock,
    BrandBlock,
    InfoPopup,
    NameBlock,
    NotifiableUsersBlock,
    OperationAreaBlock,
    PostTypeBlock,
    ConditionGroup,
  },
  mixins: [ RuleFormStepMixin ],
  computed: {
    facebookPostTypes() {
      return this.$store.getters['app/facebookPostTypes'];
    },
    facebookPostMediaTypes() {
      return this.$store.getters['app/facebookPostMediaTypes'];
    },
    instagramPostTypes() {
      return this.$store.getters['app/instagramPostTypes'];
    },
    instagramPostMediaTypes() {
      return this.$store.getters['app/instagramPostMediaTypes'];
    },
    filterIsAvailable() {
      return this.entity.hasSelectedAdAccount && this.entity.input_campaigns.length !== 0;
    },
    stopLevelOptions() {
      return [
        {
          label: this.tr('Ad'),
          value: RULE_RUN_LEVEL.AD,
          description: this.tr('Each ad is evaluated individually against its own metrics, and only the ads that meet the conditions are stopped. The other ads in the ad set keep running.'),
        },
        {
          label: this.tr('Ad set'),
          value: RULE_RUN_LEVEL.ADSET,
          description: this.tr('The conditions are evaluated against the ad set\'s combined metrics (e.g. its total reach, spend or actions), and when met, the whole ad set is stopped at once.'),
        },
      ];
    },
  },
  created() {
    if (this.isNew) {
      this.entity.facebook_post_types = this.facebookPostTypes.map(item => item.machine_name).filter(item => item !== 'reels');
      this.entity.facebook_post_media_types = this.facebookPostMediaTypes.map(item => item.machine_name);
      this.entity.instagram_post_types = this.instagramPostTypes.map(item => item.machine_name);
      this.entity.instagram_post_media_types = this.instagramPostMediaTypes.map(item => item.machine_name);
    }
  },
  methods:{
    validate() {
      if (!this.validateBlocks()) {
        return false;
      }

      if (!this.entity.settings.stop_outdated_ads || !this.entity.isEmptyConditions) {
        if (!this.entity.isValidConditions) {
          this.error(this.tr('All Filter fields must be filled in!'));
          return false;
        }
      }

      return true;
    },
  },
};
</script>
